/**
 * Rotating Shift Patterns UI.
 *
 * A shift pattern is one or more named shift variants (Morning / Late /
 * Night) plus an optional rotation schedule. Managers create the
 * pattern, preview how it resolves day-by-day, and then "Roll out" to
 * pick employees over a date range - which calls the backend's /apply
 * endpoint to materialise concrete shifts.
 *
 * The automation engines never read patterns; they only see the
 * resulting shifts. The Roll out dialog is the bridge.
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plus, X, Calendar, RefreshCcw, Send, Trash2,
} from "lucide-react";

const ROTATION_KINDS = [
  { value: "none",   label: "No rotation (always the first variant)" },
  { value: "daily",  label: "Rotate daily" },
  { value: "weekly", label: "Rotate weekly" },
];

const EMPTY = {
  name: "",
  description: "",
  variants: [
    { label: "Morning", start_time: "08:00", end_time: "16:00",
      break_minutes: 30, break_start_time: null },
  ],
  rotation_kind: "none",
  rotation_every_n: 1,
  rotation_start_date: "",
  is_active: true,
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function addDaysISO(iso, n) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function ShiftPatternsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState({ open: false, editing: null });
  const [rolloutFor, setRolloutFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shift-patterns?active_only=false");
      setRows(data || []);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="shift-patterns-page">
      <div className="flex flex-wrap items-end justify-between mb-8 gap-3">
        <div>
          <div className="eyebrow">Configuration</div>
          <h1 className="font-display text-4xl mt-1">Rotating shifts</h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
            Build a single pattern that rotates Morning, Afternoon and
            Night across the days or weeks of your team. Once you roll
            it out to your people, NextRota writes concrete shifts to
            the roster. You can change the pattern any time without
            disturbing already-published rotas.
          </p>
        </div>
        <Button data-testid="new-shift-pattern"
                onClick={() => setEditor({ open: true, editing: null })}>
          <Plus className="h-4 w-4 mr-2" /> New pattern
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-[#4B5773]">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center bg-white"
             data-testid="shift-patterns-empty">
          <Calendar className="h-6 w-6 mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-[#4B5773]">
            No rotating patterns yet. Create one to define how Morning,
            Afternoon and Night shifts rotate across your team.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto"
             data-testid="shift-patterns-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wide text-[#4B5773]">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Variants</th>
                <th className="px-4 py-3">Rotation</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.shift_pattern_id} className="border-b border-slate-100">
                  <td className="px-4 py-3 align-top">
                    <div className="font-medium">{row.name}</div>
                    {row.description && (
                      <div className="text-xs text-[#4B5773] mt-1">{row.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-wrap gap-1">
                      {(row.variants || []).map((v, idx) => (
                        <span key={idx}
                              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs">
                          <strong>{v.label}</strong>
                          <span className="text-slate-500">{v.start_time}-{v.end_time}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    {row.rotation_kind === "none"
                      ? <span className="text-xs text-slate-500">Static</span>
                      : <span className="text-xs">Every {row.rotation_every_n} {row.rotation_kind === "weekly" ? "week" : "day"}{row.rotation_every_n === 1 ? "" : "s"}{row.rotation_start_date ? `, from ${row.rotation_start_date}` : ""}</span>}
                  </td>
                  <td className="px-4 py-3 align-top">
                    {row.is_active
                      ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Active</span>
                      : <span className="text-xs text-slate-500">Archived</span>}
                  </td>
                  <td className="px-4 py-3 align-top text-right space-x-2 whitespace-nowrap">
                    <Button size="sm" variant="outline"
                            data-testid={`edit-${row.shift_pattern_id}`}
                            onClick={() => setEditor({ open: true, editing: row })}>
                      Edit
                    </Button>
                    {row.is_active && (
                      <Button size="sm"
                              data-testid={`rollout-${row.shift_pattern_id}`}
                              onClick={() => setRolloutFor(row)}>
                        <Send className="h-3 w-3 mr-1" /> Roll out
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ShiftPatternEditor
        open={editor.open}
        editing={editor.editing}
        onClose={() => setEditor({ open: false, editing: null })}
        onSaved={() => { setEditor({ open: false, editing: null }); load(); }}
      />
      <RolloutDialog
        pattern={rolloutFor}
        onClose={() => setRolloutFor(null)}
        onApplied={() => { setRolloutFor(null); toast.success("Pattern rolled out"); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor (create / edit) - variants table + rotation config + inline preview
// ---------------------------------------------------------------------------

function ShiftPatternEditor({ open, editing, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        name: editing.name || "",
        description: editing.description || "",
        variants: (editing.variants || []).map((v) => ({
          label: v.label || "",
          start_time: v.start_time || "",
          end_time: v.end_time || "",
          break_minutes: v.break_minutes ?? 0,
          break_start_time: v.break_start_time || null,
        })),
        rotation_kind: editing.rotation_kind || "none",
        rotation_every_n: editing.rotation_every_n || 1,
        rotation_start_date: editing.rotation_start_date || "",
        is_active: editing.is_active !== false,
      });
    } else {
      setForm({ ...EMPTY, variants: EMPTY.variants.map((v) => ({ ...v })) });
    }
  }, [open, editing]);

  const set = (p) => setForm((prev) => ({ ...prev, ...p }));
  const setVar = (idx, p) => setForm((prev) => ({
    ...prev,
    variants: prev.variants.map((v, i) => i === idx ? { ...v, ...p } : v),
  }));
  const addVar = () => setForm((prev) => ({
    ...prev,
    variants: [...prev.variants, {
      label: `Variant ${prev.variants.length + 1}`,
      start_time: "08:00", end_time: "16:00",
      break_minutes: 30, break_start_time: null,
    }],
  }));
  const removeVar = (idx) => setForm((prev) => ({
    ...prev,
    variants: prev.variants.length <= 1
      ? prev.variants
      : prev.variants.filter((_, i) => i !== idx),
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (form.variants.length === 0) return toast.error("At least one variant is required");
    for (const v of form.variants) {
      if (!v.label.trim()) return toast.error("Each variant needs a label");
      if (!v.start_time || !v.end_time) return toast.error("Each variant needs start and end times");
      if (v.end_time <= v.start_time) return toast.error(`Variant '${v.label}' must have start before end`);
    }
    setBusy(true);
    const payload = {
      name: form.name.trim(),
      description: form.description || null,
      variants: form.variants.map((v) => ({
        label: v.label.trim(),
        start_time: v.start_time, end_time: v.end_time,
        break_minutes: Number(v.break_minutes) || 0,
        break_start_time: v.break_start_time || null,
      })),
      rotation_kind: form.rotation_kind,
      rotation_every_n: Math.max(1, Math.min(12, Number(form.rotation_every_n) || 1)),
      rotation_start_date: form.rotation_kind === "none" ? null : (form.rotation_start_date || null),
      is_active: !!form.is_active,
    };
    try {
      if (editing) {
        await api.patch(`/shift-patterns/${editing.shift_pattern_id}`, payload);
      } else {
        await api.post("/shift-patterns", payload);
      }
      toast.success(editing ? "Pattern updated" : "Pattern created");
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm("Archive this pattern? Existing shifts already written stay untouched.")) return;
    try {
      await api.delete(`/shift-patterns/${editing.shift_pattern_id}`);
      toast.success("Pattern archived");
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not archive");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto"
                     data-testid="shift-pattern-editor">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit rotating pattern" : "New rotating pattern"}</DialogTitle>
          <DialogDescription>
            Define one or more shift variants and how they rotate across
            days or weeks. Preview the result before you roll it out.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-5" onSubmit={submit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="eyebrow">Pattern name</Label>
              <Input value={form.name} required data-testid="sp-name"
                     onChange={(e) => set({ name: e.target.value })}
                     placeholder="e.g. 3-week shift rotation" />
            </div>
            <div>
              <Label className="eyebrow">Status</Label>
              <div className="flex items-center gap-2 h-11 mt-1">
                <Switch checked={form.is_active} data-testid="sp-active"
                        onCheckedChange={(v) => set({ is_active: v })} />
                <span className="text-sm">{form.is_active ? "Active" : "Archived"}</span>
              </div>
            </div>
          </div>
          <div>
            <Label className="eyebrow">Description (optional)</Label>
            <Input value={form.description} data-testid="sp-description"
                   onChange={(e) => set({ description: e.target.value })}
                   placeholder="Short note for your colleagues" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="eyebrow">Shift variants</Label>
              <Button type="button" size="sm" variant="outline"
                      data-testid="add-variant" onClick={addVar}>
                <Plus className="h-3 w-3 mr-1" /> Add variant
              </Button>
            </div>
            <div className="space-y-2" data-testid="variants-list">
              {form.variants.map((v, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3 bg-slate-50/40">
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-12 md:col-span-3">
                      <Label className="text-xs">Label</Label>
                      <Input value={v.label} required
                             data-testid={`var-${i}-label`}
                             onChange={(e) => setVar(i, { label: e.target.value })}
                             placeholder="Morning" />
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-xs">Start</Label>
                      <Input type="time" value={v.start_time}
                             data-testid={`var-${i}-start`}
                             onChange={(e) => setVar(i, { start_time: e.target.value })} />
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-xs">End</Label>
                      <Input type="time" value={v.end_time}
                             data-testid={`var-${i}-end`}
                             onChange={(e) => setVar(i, { end_time: e.target.value })} />
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-xs">Break (min)</Label>
                      <Input type="number" min="0" value={v.break_minutes}
                             data-testid={`var-${i}-break-min`}
                             onChange={(e) => setVar(i, { break_minutes: Number(e.target.value) || 0 })} />
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-xs">Break start (optional)</Label>
                      <Input type="time" value={v.break_start_time || ""}
                             data-testid={`var-${i}-break-start`}
                             onChange={(e) => setVar(i, { break_start_time: e.target.value || null })}
                             placeholder="Auto" />
                    </div>
                    <div className="col-span-12 md:col-span-1 flex md:justify-end">
                      <Button type="button" size="sm" variant="ghost"
                              data-testid={`var-${i}-remove`}
                              disabled={form.variants.length === 1}
                              onClick={() => removeVar(i)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#4B5773] mt-2">
              Leave break start blank and NextRota will centre it on the
              shift midpoint. You can override that any time.
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <Label className="eyebrow">Rotation</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
              <div>
                <Label className="text-xs">Rotate</Label>
                <Select value={form.rotation_kind}
                         onValueChange={(v) => set({ rotation_kind: v })}>
                  <SelectTrigger className="h-10 mt-1" data-testid="sp-rotation-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROTATION_KINDS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}
                                   data-testid={`sp-rot-${opt.value}`}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.rotation_kind !== "none" && (
                <>
                  <div>
                    <Label className="text-xs">
                      Every N {form.rotation_kind === "weekly" ? "week(s)" : "day(s)"}
                    </Label>
                    <Input type="number" min="1" max="12"
                           value={form.rotation_every_n}
                           data-testid="sp-every-n"
                           onChange={(e) => set({ rotation_every_n: Number(e.target.value) || 1 })} />
                  </div>
                  <div>
                    <Label className="text-xs">Anchor start date</Label>
                    <Input type="date" value={form.rotation_start_date}
                           data-testid="sp-start-date"
                           onChange={(e) => set({ rotation_start_date: e.target.value })} />
                  </div>
                </>
              )}
            </div>
            <p className="text-xs text-[#4B5773] mt-3">
              The anchor start date is when variant 1 begins. Dates
              before this anchor fall back to variant 1.
            </p>
          </div>

          {editing && (
            <PreviewBlock patternId={editing.shift_pattern_id} />
          )}

          <DialogFooter className="gap-2">
            {editing && (
              <Button type="button" variant="ghost"
                      data-testid="sp-archive" onClick={remove}>
                <Trash2 className="h-4 w-4 mr-1" /> Archive
              </Button>
            )}
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={busy}
                      data-testid="sp-cancel">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={busy} data-testid="sp-save">
              {editing ? "Save changes" : "Create pattern"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Inline preview - only available when editing an existing pattern, because
// the resolver runs server-side against the saved row.
// ---------------------------------------------------------------------------

function PreviewBlock({ patternId }) {
  const [fromDate, setFromDate] = useState(todayISO());
  const [toDate, setToDate] = useState(addDaysISO(todayISO(), 27));
  const [offset, setOffset] = useState(0);
  const [days, setDays] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(
        `/shift-patterns/${patternId}/preview?from_date=${fromDate}&to_date=${toDate}&employee_offset=${offset}`);
      setDays(data.days || []);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Preview failed");
    } finally { setBusy(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [patternId]);

  const variantColours = useMemo(() => {
    const map = new Map();
    let palette = ["bg-amber-100 text-amber-900", "bg-sky-100 text-sky-900",
                    "bg-violet-100 text-violet-900", "bg-emerald-100 text-emerald-900",
                    "bg-rose-100 text-rose-900", "bg-teal-100 text-teal-900",
                    "bg-orange-100 text-orange-900"];
    let n = 0;
    for (const d of days) {
      const lbl = d.variant?.label || "(none)";
      if (!map.has(lbl)) { map.set(lbl, palette[n % palette.length]); n++; }
    }
    return map;
  }, [days]);

  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="sp-preview">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
        <Label className="eyebrow">Preview</Label>
        <Button type="button" size="sm" variant="outline"
                data-testid="sp-preview-refresh" onClick={refresh} disabled={busy}>
          <RefreshCcw className="h-3 w-3 mr-1" /> Refresh
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={fromDate} data-testid="sp-preview-from"
                 onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={toDate} data-testid="sp-preview-to"
                 onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Employee offset</Label>
          <Input type="number" min="0" value={offset}
                 data-testid="sp-preview-offset"
                 onChange={(e) => setOffset(Number(e.target.value) || 0)} />
        </div>
      </div>
      {days.length > 0 ? (
        <div className="grid grid-cols-7 gap-1 text-xs" data-testid="sp-preview-grid">
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((h) => (
            <div key={h} className="text-center font-medium text-slate-500 pb-1">{h}</div>
          ))}
          {(() => {
            if (days.length === 0) return null;
            const firstDow = days[0].weekday;
            const blanks = Array(firstDow).fill(null);
            const all = [...blanks, ...days];
            return all.map((d, i) => {
              if (!d) return <div key={`b-${i}`} />;
              const lbl = d.variant?.label || "-";
              const cls = variantColours.get(lbl) || "bg-slate-100 text-slate-700";
              const dayNum = d.date.slice(-2);
              return (
                <div key={d.date}
                     className={`rounded p-1 text-center ${cls}`}
                     title={`${d.date}: ${lbl} ${d.variant ? `${d.variant.start_time}-${d.variant.end_time}` : ""}`}>
                  <div className="font-medium">{dayNum}</div>
                  <div className="truncate text-[10px] opacity-80">{lbl}</div>
                </div>
              );
            });
          })()}
        </div>
      ) : (
        <p className="text-xs text-slate-500">No preview data yet.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Roll out dialog - picks employees, date range, optional per-employee offset,
// calls /apply.
// ---------------------------------------------------------------------------

function RolloutDialog({ pattern, onClose, onApplied }) {
  const [employees, setEmployees] = useState([]);
  const [picked, setPicked] = useState({});  // {emp_id: offset}
  const [fromDate, setFromDate] = useState(todayISO());
  const [toDate, setToDate] = useState(addDaysISO(todayISO(), 27));
  const [skipExisting, setSkipExisting] = useState(true);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pattern) return;
    api.get("/employees?active_only=true").then(({ data }) => {
      setEmployees(data || []);
      setPicked({});
    }).catch(() => setEmployees([]));
  }, [pattern]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return employees;
    return employees.filter((e) =>
      (e.name || "").toLowerCase().includes(f)
      || (e.email || "").toLowerCase().includes(f));
  }, [employees, filter]);

  const togglePick = (eid) => setPicked((prev) => {
    if (eid in prev) {
      const { [eid]: _, ...rest } = prev;
      return rest;
    }
    return { ...prev, [eid]: 0 };
  });
  const setOffset = (eid, val) => setPicked((prev) =>
    prev[eid] !== undefined ? { ...prev, [eid]: Math.max(0, Number(val) || 0) } : prev);

  const apply = async () => {
    const ids = Object.keys(picked);
    if (ids.length === 0) return toast.error("Pick at least one employee");
    if (!fromDate || !toDate) return toast.error("Pick a date range");
    setBusy(true);
    try {
      const { data } = await api.post(
        `/shift-patterns/${pattern.shift_pattern_id}/apply`, {
          employee_ids: ids,
          from_date: fromDate, to_date: toDate,
          employee_offsets: picked,
          skip_existing: skipExisting,
          override_lock: false,
        });
      toast.success(`Wrote ${data.written} shift${data.written === 1 ? "" : "s"}. Skipped ${data.skipped_existing} existing, ${data.skipped_leave} on leave.`);
      onApplied();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Apply failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!pattern} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto"
                     data-testid="rollout-dialog">
        <DialogHeader>
          <DialogTitle>Roll out: {pattern?.name}</DialogTitle>
          <DialogDescription>
            Pick the employees and the date range. NextRota will write
            the resolved shifts to the roster. Days where an employee
            already has a shift, or is on approved leave, are skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="eyebrow">From</Label>
              <Input type="date" value={fromDate} data-testid="rollout-from"
                     onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div>
              <Label className="eyebrow">To</Label>
              <Input type="date" value={toDate} data-testid="rollout-to"
                     onChange={(e) => setToDate(e.target.value)} />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={skipExisting} data-testid="rollout-skip-existing"
                    onCheckedChange={setSkipExisting} />
            <span className="text-sm">Skip days where the employee already has a shift</span>
          </div>

          <div>
            <Label className="eyebrow">Employees</Label>
            <Input value={filter} placeholder="Filter by name or email"
                   data-testid="rollout-filter"
                   onChange={(e) => setFilter(e.target.value)} />
            <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-slate-200"
                 data-testid="rollout-emp-list">
              {filtered.map((e) => {
                const isPicked = e.employee_id in picked;
                return (
                  <div key={e.employee_id}
                       className={`flex items-center justify-between px-3 py-2 border-b border-slate-100 ${isPicked ? "bg-emerald-50/60" : ""}`}>
                    <label className="flex items-center gap-2 cursor-pointer flex-1">
                      <input type="checkbox" checked={isPicked}
                             data-testid={`rollout-emp-${e.employee_id}`}
                             onChange={() => togglePick(e.employee_id)} />
                      <span className="text-sm">{e.name}</span>
                      <span className="text-xs text-slate-500">{e.team_name || ""}</span>
                    </label>
                    {isPicked && pattern?.rotation_kind !== "none" && (
                      <div className="flex items-center gap-1 ml-2">
                        <span className="text-xs text-slate-500">offset</span>
                        <Input type="number" min="0" className="h-8 w-16"
                               value={picked[e.employee_id]}
                               data-testid={`rollout-offset-${e.employee_id}`}
                               onChange={(ev) => setOffset(e.employee_id, ev.target.value)} />
                      </div>
                    )}
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="px-3 py-6 text-sm text-slate-500 text-center">
                  No employees match this filter.
                </div>
              )}
            </div>
            <p className="text-xs text-[#4B5773] mt-2">
              The offset lets two employees on the same rotation start
              on different weeks. Offset 0 = anchor variant; offset 1 =
              one cycle ahead.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button variant="outline" disabled={busy}
                    data-testid="rollout-cancel">Cancel</Button>
          </DialogClose>
          <Button onClick={apply} disabled={busy || Object.keys(picked).length === 0}
                  data-testid="rollout-apply">
            <Send className="h-4 w-4 mr-2" />
            Roll out to {Object.keys(picked).length} employee{Object.keys(picked).length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
